import queryString from 'query-string';
import type { ChangeEvent } from 'react';
import { useCallback, useMemo } from 'react';

import { Input } from '#/components/Input/Input';
import { InputIconAffix } from '#/components/Input/affixes/InputIconAffix';
import { Tabs } from '#/components/Tabs/Tabs';
import { createTab } from '#/components/Tabs/createTab';
import { TileGrid } from '#/components/TileGrid/TileGrid';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { useScrollToOnce } from '#/hooks/useScrollToOnce';
import type { CmsApiTileGridBlockData } from '#/modules/cms/api/types/blocks/CmsApiTileGridBlock';
import type { CmsBlockMappingComponentProps } from '#/modules/cms/blocks/CmsBlockMapping';
import { CmsTileModal } from '#/modules/cms/modals/TileModal/CmsTileModal';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import {
  CmsTileGridBlockHolder,
  CmsTileGridBlockInputHolder,
  CmsTileGridBlockTabSwitcherHolder,
} from './CmsTileGridBlock.styled';
import { TileGridCarousel } from './TileGridCarousel/TileGridCarousel';
import { getCmsTileGridSectionQueryParamName } from './helpers/getCmsTileGridSectionQueryParamName';
import { getCmsTileGridTileQueryParamName } from './helpers/getCmsTileGridTileQueryParamName';
import type {
  IdentifiedTileGridBlockSection,
  IdentifiedTileGridBlockTile,
} from './hooks/useIdentifiedTileGridBlockData';
import { useIdentifiedTileGridBlockData } from './hooks/useIdentifiedTileGridBlockData';
import { useTileGridBlockTileNavigation } from './hooks/useTileGridBlockTileNavigation';
import { useTileGridSearch } from './hooks/useTileGridSearch';

type Props = CmsBlockMappingComponentProps<CmsApiTileGridBlockData>;

export function CmsTileGridBlock({ blockId, blockIndex, data }: Props) {
  const { identifiedSections, identifiedTiles, identifiedSectionBySlug } =
    useIdentifiedTileGridBlockData({ blockId, sections: data.sections });

  // Section

  const [sectionSlug, setSectionSlug] = useQueryParamState({
    name: getCmsTileGridSectionQueryParamName({ blockId }),
  });

  const identifiedSection =
    (sectionSlug ? identifiedSectionBySlug[sectionSlug] : null) ??
    identifiedSections[0];

  // Tile

  const [tileSlug, setTileSlug] = useQueryParamState({
    name: getCmsTileGridTileQueryParamName({ blockId }),
  });

  const identifiedTile = useMemo(
    () =>
      identifiedSection.identifiedTiles.find(
        (tile) => tile.tileSlug === tileSlug,
      ),
    [identifiedSection.identifiedTiles, tileSlug],
  );

  useScrollToOnce({ id: identifiedTile?.tileDOMElementId });

  // Search

  const [rawQuery, setQuery] = useQueryParamState({
    name: `tile-search-${blockId}`,
  });

  const query = rawQuery ?? '';

  const changeTileSearchQuery = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setQuery(event.target.value);
    },
    [setQuery],
  );

  const { identifiedSearchTiles } = useTileGridSearch({
    query,
    identifiedTiles,
  });

  const tabs = identifiedSections.map((section) =>
    createTab({
      prefix: 'cms-tile-grid',
      value: section.sectionSlug,
      label: section.name,
      qaId: `cms-tile-grid-${section.name}`,
    }),
  );

  const selectedTab = tabs.find(
    (tab) => tab.value === identifiedSection.sectionSlug,
  );

  // Navigation

  const identifiedTilesForNavigation: IdentifiedTileGridBlockTile[] =
    useMemo(() => {
      if (identifiedSearchTiles) return identifiedSearchTiles;
      if (!identifiedTile) return [];

      return identifiedSections[identifiedTile.sectionIndex].identifiedTiles;
    }, [identifiedSearchTiles, identifiedSections, identifiedTile]);

  const { hasPreviousTile, previousTile, hasNextTile, nextTile, closeTile } =
    useTileGridBlockTileNavigation({
      identifiedTiles: identifiedTilesForNavigation,
      tileSlug,
      onSelectTile: setTileSlug,
    });

  // Tab click

  const selectSection = useCallback(
    (section: IdentifiedTileGridBlockSection) => {
      setQuery('');
      setSectionSlug(section.sectionSlug);
    },
    [setQuery, setSectionSlug],
  );

  // Tile click

  const clickTile = useCallback(
    (tile: IdentifiedTileGridBlockTile) => {
      trackClicked('Tile Grid Tile', {
        tile_index: tile.tileIndex,
        tile_search: query || undefined,
        tile_section: identifiedSections[tile.sectionIndex].name,
        tile_heading: tile.heading,
      });

      setTileSlug(tile.tileSlug);
    },
    [query, identifiedSections, setTileSlug],
  );

  // Show

  const identifiedTilesToShow =
    identifiedSearchTiles ?? identifiedSection.identifiedTiles;

  return (
    <CmsTileGridBlockHolder data-qa-id="cms-tile-grid">
      {data.showSearch && (
        <CmsTileGridBlockInputHolder>
          <Input
            inputProps={{
              id: `cms-tile-grid-${blockId}`,
              value: query,
              onChange: changeTileSearchQuery,
              placeholder: 'Search...',
              qaId: 'cms-tile-grid-search-input',
            }}
            prefix={<InputIconAffix iconName="search" />}
            affixVariant="transparent"
          />
        </CmsTileGridBlockInputHolder>
      )}

      {identifiedSections.length > 1 && (
        <CmsTileGridBlockTabSwitcherHolder>
          <Tabs
            tabs={tabs}
            value={query ? undefined : identifiedSection.sectionSlug}
            onSelectTab={(slug) => selectSection(identifiedSectionBySlug[slug])}
            variant="marker"
            layout="horizontal"
          />
        </CmsTileGridBlockTabSwitcherHolder>
      )}

      {identifiedTilesToShow.length === 0 && (
        <div>No results for this search</div>
      )}

      {identifiedTilesToShow.length > 0 && data.displayAsSingleRow && (
        <TileGridCarousel
          key={identifiedSection.sectionSlug}
          tiles={identifiedTilesToShow.map((tile) => ({
            id: tile.tileDOMElementId,
            imageData: tile.thumbnail,
            heading: tile.heading,
            subHeading: tile.subHeading,
            description: tile.description,
            tileHref: queryString.stringifyUrl({
              url: typeof window !== 'undefined' ? window.location.href : '',
              query: {
                [getCmsTileGridTileQueryParamName({ blockId })]: tile.tileSlug,
              },
            }),
          }))}
          tilesPerRow={data.tilesPerRow}
          onClickTile={(index) => clickTile(identifiedTilesToShow[index])}
          variant={data.imageSize}
        />
      )}

      {identifiedTilesToShow.length > 0 && !data.displayAsSingleRow && (
        <TileGrid
          id={selectedTab?.value ?? ''}
          key={identifiedSection.sectionSlug}
          tiles={identifiedTilesToShow.map((tile) => ({
            id: tile.tileDOMElementId,
            imageData: tile.thumbnail,
            heading: tile.heading,
            subHeading: tile.subHeading,
            description: tile.description,
            tileHref: queryString.stringifyUrl({
              url: typeof window !== 'undefined' ? window.location.href : '',
              query: {
                [getCmsTileGridTileQueryParamName({ blockId })]: tile.tileSlug,
              },
            }),
          }))}
          tilesPerRow={data.tilesPerRow}
          onClickTile={(index) => clickTile(identifiedTilesToShow[index])}
          variant={data.imageSize}
        />
      )}

      {identifiedTile && (
        <CmsTileModal
          key={identifiedTile.tileSlug}
          blockId={blockId}
          sectionIndex={identifiedTile.sectionIndex}
          heading={identifiedTile.heading}
          subHeading={identifiedTile.subHeading}
          description={identifiedTile.description}
          caption={identifiedTile.image.caption ?? undefined}
          imageData={identifiedTile.image}
          buttonData={identifiedTile.button ?? undefined}
          onClose={closeTile}
          hasPrevious={hasPreviousTile}
          onPrevious={previousTile}
          hasNext={hasNextTile}
          onNext={nextTile}
          analyticsData={{
            eventName: 'CMS Block',
            eventProperties: {
              block_position: blockIndex,
              block_type: 'Tile Grid',
            },
          }}
        />
      )}
    </CmsTileGridBlockHolder>
  );
}
